import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../shared/TailwindComponents/Table';
import { PiFileTextLight, PiUserLight, PiBuildingLight } from 'react-icons/pi';
import { fetchLeads } from '../../store/actions/Pipeline/PipelineActions';
import Badge from '../shared/TailwindComponents/Badge';
import { formatDate, getStatusColor } from '../../utils/functions';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import LeadGridItem from './LeadGridItem';
import Skeleton from '../shared/TailwindComponents/Skeleton';

const Pipeline: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { leads, isLoading } = useSelector((state: RootState) => state.pipeline);
  const userId = useSelector((state: RootState) => state.user.userInfo.UserID);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [sortValue, setSortValue] = useState('created_at:desc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  useEffect(() => {
    const [sortBy, sortOrder] = sortValue.split(':');
    dispatch(fetchLeads({ lawyerId: userId, searchTerm: searchQuery, filters: selectedFilters, sortBy, sortOrder }));
  }, [dispatch, userId, searchQuery, selectedFilters, sortValue]);

  const handleSearchChange = (query: string) => setSearchQuery(query);
  const handleFilterChange = (filterName: string, selectedValues: string[]) =>
    setSelectedFilters((prev) => ({ ...prev, [filterName]: selectedValues }));
  const handleSortChange = (value: string) => setSortValue(value);

  const filters = [
    {
      id: 'status',
      name: t('Status'),
      options: [
        { value: 'Pending', label: t('Pending') },
        { value: 'Accepted', label: t('Accepted') },
        { value: 'Rejected', label: t('Rejected') },
      ],
    },
  ];

  const sortOptions = [
    { value: 'created_at:desc', label: t('Submitted At Descending'), current: true },
    { value: 'created_at:asc', label: t('Submitted At Ascending'), current: false },
    { value: 'budget:desc', label: t('Budget Descending'), current: false },
    { value: 'budget:asc', label: t('Budget Ascending'), current: false },
  ];

  const columns = [
    t('Client'),
    t('Case'),
    t('Case Type'),
    t('Location'),
    t('Budget'),
    t('Timeframe'),
    t('Status'),
    t('Submitted At'),
    t('Actions'),
  ];

  const tableData = leads.map((lead: any) => ({
    id: lead.id,
    [t('Client')]: (
      <div className="flex items-center gap-2">
        {lead.client_type === 'Individual' ? <PiUserLight /> : <PiBuildingLight />}
        <span>{lead.client_name || 'N/A'}</span>
      </div>
    ),
    [t('Case')]: lead.case_name || 'N/A',
    [t('Case Type')]: lead.case_type || 'N/A',
    [t('Location')]: lead.case_location || 'N/A',
    [t('Budget')]: `$${lead.budget}`,
    [t('Timeframe')]: lead.timeframe,
    [t('Status')]: <Badge color={getStatusColor(lead.status) as BadgeColor}>{lead.status}</Badge>,
    [t('Submitted At')]: formatDate(lead.created_at),
    [t('Actions')]: (
      <button onClick={() => history.push(`/pipeline/${lead.id}`)}>
        <PiFileTextLight className="h-4 w-4" />
      </button>
    ),
  }));

  return (
    <div className="bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark transition-colors duration-300 flex flex-col h-full overflow-hidden">
      <Header dropdownActions={[]} subscriptionActive={true} setViewMode={setViewMode} />
      <FilterBarWithTabs
        itemCount={leads.length}
        totalItemCount={leads.length}
        itemCountLabel="leads"
        filters={filters}
        sortOptions={sortOptions}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onSortChange={handleSortChange}
        onSearchChange={handleSearchChange}
        sortValue={sortValue}
        actions={[]}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />
      {/* Measured. The grid branch below keeps a scroller of its own; the table
          branch owns the only one on its side. Sharing one page scroller
          between them is what nested a scrollbar inside the table's. */}
      <div className="flex-1 min-h-0 flex flex-col px-4 py-2">
        {isLoading ? (
          <Skeleton variant="grid" rows={3} />
        ) : leads.length > 0 ? (
          viewMode === 'table' ? (
            <Table
              columns={columns}
              data={tableData}
              onRowClick={(row) => history.push(`/pipeline/${row.id}`)}
              total_items={leads.length}
              total_showing_items={leads.length}
              setIsShowing={true}
              showingLabel={t('leads')}
              /* The box above scrolls - the grid view needs it to. So the
                 table renders full-height inside it rather than capping
                 itself and nesting a second scroller in the first. */
              max="max-h-none"
              min="min-h-0"
              actions={[
                {
                  label: t('View'),
                  onClick: (row) => history.push(`/pipeline/${row.id}`),
                  icon: <PiFileTextLight className="h-4 w-4" />,
                },
              ]}
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 flex-1 min-h-0 overflow-y-auto custom-scrollbar-main pb-4">
              {leads.map((lead: any) => (
                <LeadGridItem
                  key={lead.id}
                  lead={lead}
                  onClick={() => history.push(`/pipeline/${lead.id}`)}
                />
              ))}
            </div>
          )
        ) : (
          <p className="text-center text-gray-500 dark:text-gray-400">{t('No leads available')}</p>
        )}
      </div>
    </div>
  );
};

export default Pipeline;